import type {CreateReviewCommentMutationData} from '@github-ui/pull-requests/use-create-review-comment-mutation'
import {
  CommentSubjectType,
  type DiffLineWithSelectionInfo,
  type DiffOrientation,
  type DiffRange,
  type NewTreeEntry,
  type OldTreeEntry,
} from '../types'
import {isContextDiffLine} from './line-helpers'
import type {LineRange} from '@github-ui/diffs/types'

export type ReviewCommentFactoryOpts = {
  filePath: string
  newTreeEntry?: NewTreeEntry | null | undefined
  oldTreeEntry?: OldTreeEntry | null | undefined
  effectiveDiffRange: DiffRange
  submitBatch?: boolean | undefined
  text: string
}

/**
 * Factory for building payloads used to create review comments.
 *
 * This class centralizes construction of CreateReviewCommentMutationData for
 * file-level comments, single-line comments, and multiline comments. It uses
 * the provided diff range and tree entry metadata to compute correct paths
 * and commit OIDs for the positioning information expected by the
 * useCreateReviewCommentMutation.
 */
export class ReviewCommentFactory {
  #baseCommitOid: string
  #filePath: string
  #headCommitOid: string
  #newTreeEntry: NewTreeEntry | null | undefined
  #oldTreeEntry: OldTreeEntry | null | undefined
  #submitBatch: boolean | undefined
  #text: string

  /**
   * Create a new ReviewCommentFactory.
   *
   * @param opts - Options for constructing request payloads
   * @param opts.filePath - The file path currently shown in the diff
   * @param opts.newTreeEntry - New tree entry metadata for the file (may be null/undefined)
   * @param opts.oldTreeEntry - Old tree entry metadata for the file (may be null/undefined)
   * @param opts.effectiveDiffRange - The diff range selection which supplies base and head commit OIDs
   * @param opts.submitBatch - Whether the comment should be submitted as part of a batch (pending review) or immediately
   * @param opts.text - The comment text to submit
   */
  constructor({filePath, newTreeEntry, oldTreeEntry, effectiveDiffRange, submitBatch, text}: ReviewCommentFactoryOpts) {
    this.#baseCommitOid = effectiveDiffRange.baseOid
    this.#filePath = filePath
    this.#headCommitOid = effectiveDiffRange.headOid
    this.#newTreeEntry = newTreeEntry
    this.#oldTreeEntry = oldTreeEntry
    this.#submitBatch = submitBatch
    this.#text = text
  }

  /**
   * Build request payload for a file-level review comment.
   *
   * The returned object conforms to CreateReviewCommentMutationData and does not set
   * positioning data. With positioning data set, the comment can't accurately be placed on a commit
   * if the file is either brand new or deleted. The file positioning info is not required to make a file level
   * comment, so it is omitted.
   */
  fileRequestData(): CreateReviewCommentMutationData {
    return {
      ...this.#commonRequestData(),
      path: this.#filePath,
      subjectType: CommentSubjectType.File,
    }
  }

  /**
   * Build request payload for a single-line review comment.
   *
   * @param line - A diff line containing blobLineNumber and selection info
   * @param isLeftSide - True when the user selected the left side of the diff; used to determine the comment side
   * @returns CreateReviewCommentMutationData configured for a line-level comment
   */
  lineRequestData(line: DiffLineWithSelectionInfo, isLeftSide: boolean): CreateReviewCommentMutationData {
    const diffSide = isLeftSide && !isContextDiffLine(line) ? 'left' : 'right'

    return {
      ...this.#commonRequestData(),
      line: line.blobLineNumber,
      path: this.#path(diffSide),
      positioning: {
        type: CommentSubjectType.Line,
        baseCommitOid: this.#baseCommitOid,
        headCommitOid: this.#headCommitOid,
        path: this.#path(diffSide),
        line: line.blobLineNumber,
        commitOid: this.#commitOid(diffSide),
      },
      side: diffSide,
      subjectType: CommentSubjectType.Line,
    }
  }

  /**
   * Build request payload for a multiline review comment.
   *
   * @param selectedLineRange - A LineRange describing the start/end lines and orientations
   * @returns CreateReviewCommentMutationData configured for a multiline comment
   *
   * Notes:
   * - startOrientation and endOrientation are used to determine which side ('left'|'right')
   *   each end of the selection belongs to.
   */
  multilineRequestData(selectedLineRange: LineRange): CreateReviewCommentMutationData {
    const {endLineNumber, endOrientation, startLineNumber, startOrientation} = selectedLineRange
    const endLine = endLineNumber
    const endSide = endOrientation === 'left' ? 'left' : 'right'
    const startLine = startLineNumber
    const startSide = startOrientation === 'left' ? 'left' : 'right'

    return {
      ...this.#commonRequestData(),
      line: endLine,
      path: this.#path(endSide),
      positioning: {
        baseCommitOid: this.#baseCommitOid,
        headCommitOid: this.#headCommitOid,
        type: CommentSubjectType.Multiline,
        startPath: this.#path(startSide),
        startLine,
        startCommitOid: this.#commitOid(startSide),
        endPath: this.#path(endSide),
        endLine,
        endCommitOid: this.#commitOid(endSide),
      },
      side: endSide,
      startLine,
      startSide,
      subjectType: CommentSubjectType.Multiline,
    }
  }

  #path(diffSide: DiffOrientation): string {
    if (
      diffSide === 'left' &&
      this.#oldTreeEntry?.path &&
      this.#newTreeEntry?.path &&
      this.#oldTreeEntry.path !== this.#newTreeEntry.path
    ) {
      return this.#oldTreeEntry.path
    }

    return this.#filePath
  }

  #commitOid(diffSide: DiffOrientation): string {
    if (diffSide === 'left') {
      return this.#baseCommitOid
    }

    return this.#headCommitOid
  }

  #commonRequestData(): Pick<
    CreateReviewCommentMutationData,
    'comparisonEndOid' | 'comparisonStartOid' | 'text' | 'submitBatch'
  > {
    return {
      comparisonEndOid: this.#headCommitOid,
      comparisonStartOid: this.#baseCommitOid,
      text: this.#text,
      submitBatch: this.#submitBatch,
    }
  }
}
